import { z } from 'zod';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');

// ── Query ─────────────────────────────────────────────────────────────────────

export const commandCenterQuerySchema = z.object({
  // comma-separated company UUIDs
  companyIds: z
    .string()
    .min(1)
    .transform((s) => s.split(',').map((x) => x.trim()).filter(Boolean))
    .pipe(z.array(z.string().uuid()).min(1)),
  from: isoDate,
  to: isoDate,
});
export type CommandCenterQuery = z.infer<typeof commandCenterQuerySchema>;

// ── Time series (consumed directly by Recharts) ──────────────────────────────

export interface TimeSeriesPoint {
  companyId: string;
  companyName: string;
  color: string;
  values: number[];
}

export interface TimeSeriesResponse {
  months: string[]; // ["2026-01", "2026-02", ...]
  series: TimeSeriesPoint[];
  combined: number[]; // sum across series per month
}

// ── KPIs ──────────────────────────────────────────────────────────────────────

export interface CommandCenterKpis {
  revenue: string;
  expenses: string;
  netIncome: string;
  cash: string;
  totalAssets: string;
  totalLiabilities: string;
  /** Period-over-period percentage change vs the immediately-prior equal period. */
  deltas: {
    revenue: number | null;
    expenses: number | null;
    netIncome: number | null;
  };
}

// ── Expense breakdown (horizontal bar) ───────────────────────────────────────

export interface ExpenseBreakdownItem {
  key: string;
  label: string;
  amount: number;
}
export interface ExpenseBreakdownResponse {
  items: ExpenseBreakdownItem[];
}

// ── Company summary table ────────────────────────────────────────────────────

export interface CompanySummaryRow {
  companyId: string;
  companyName: string;
  color: string;
  revenue: string;
  expenses: string;
  netIncome: string;
  cash: string;
}

export interface CompanySummaryResponse {
  rows: CompanySummaryRow[];
  totals: {
    revenue: string;
    expenses: string;
    netIncome: string;
    cash: string;
  };
}

/** One company's share of a balance, for a composition pie. */
export interface BalanceSplitSlice {
  companyId: string;
  companyName: string;
  /** Same company colour used by every other Command Center chart. */
  color: string;
  amount: string;
  /** Share of the total, 0-100, rounded to one decimal. */
  percent: number;
}

/**
 * Where the group's assets and liabilities sit, by company.
 *
 * A balance, not a period figure, so it is `asOf` a single date rather than a
 * range — "what do we hold today", not "what moved this quarter".
 *
 * A company whose balance is zero or negative is EXCLUDED from its pie: a pie
 * slice cannot represent a negative, and drawing one at zero adds a legend
 * entry for nothing. `excluded` names them so the omission is visible rather
 * than silent.
 */
export interface BalanceSplitResponse {
  asOf: string;
  assets: {
    total: string;
    slices: BalanceSplitSlice[];
    excluded: { companyId: string; companyName: string; amount: string }[];
  };
  liabilities: {
    total: string;
    slices: BalanceSplitSlice[];
    excluded: { companyId: string; companyName: string; amount: string }[];
  };
}
